import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { Reveal } from '../components/motion';
import SEO from '../components/SEO';
import { SectionTag, ArrowBtn, PageHero, FAQ } from '../components/bits';
import { useLead } from '../components/LeadModal';
import { useContent } from '../lib/content';
import { SERVICES, ENTERPRISE_FAQ, IMAGES } from '../content/data';

const PILLARS = [
  { t: 'Talent Acquisition', d: 'See capability before you hire it.', to: '/enterprises/talent' },
  { t: 'CSR Routing', d: 'Make your CSR allocation work across multiple outcomes.', to: '/enterprises/csr' },
  { t: 'Marketing Cell', d: 'Digital reach backed by intelligence.', to: '/enterprises/marketing-cell' },
  { t: 'Research & Diagnostics', d: 'Understand before you invest.', to: '/enterprises/research' },
  { t: 'Enterprise Membership', d: 'One relationship. Seven strategic advantages.', to: '/enterprises/membership' },
  { t: 'Institutional Partnerships', d: 'Build a stronger digital pathway for your students.', to: '/institutions' },
];

export default function Enterprises() {
  const { open } = useLead();
  const { ctList } = useContent();
  return (
    <div data-testid="enterprises-page">
    <SEO title="For Enterprises" description="Talent, technology, intelligence and impact through one institutional ecosystem." />
      <PageHero
        ck="enterprises"
        dark
        tag="For Enterprises"
        lines={<>Talent. Technology. Intelligence. <span className="font-serif italic font-normal text-gold">Impact.</span></>}
        intro="DITEC brings these capabilities together through an institutional ecosystem designed for enterprises seeking stronger access to digital talent, research, technology execution and measurable impact."
      >
        <ArrowBtn to="/enterprises/membership" variant="gold" testId="ent-cta-member">Become a DITEC Member</ArrowBtn>
        <ArrowBtn to="/enterprises/csr" variant="ghostLight" testId="ent-cta-csr">Explore CSR Routing</ArrowBtn>
      </PageHero>

      {/* Why engage */}
      <section className="bg-paper py-20 sm:py-28">
        <div className="wrap grid gap-10 lg:grid-cols-2 lg:gap-20">
          <div>
            <SectionTag>01 — Why Enterprises Engage With DITEC</SectionTag>
            <Reveal delay={0.05}><h2 className="text-h2 mt-5 font-semibold">One Connected <span className="font-serif italic font-normal text-electric">Relationship.</span></h2></Reveal>
          </div>
          <Reveal delay={0.1}>
            <p className="text-base leading-relaxed text-navy/75 sm:text-lg">Enterprise challenges rarely arrive one at a time.</p>
            <p className="mt-4 text-base leading-relaxed text-navy/75 sm:text-lg">You may need specialised talent while also needing technology support, CSR deployment, digital visibility or research.</p>
            <p className="mt-4 text-h3 font-semibold text-navy">DITEC is designed to address these requirements through one connected relationship.</p>
          </Reveal>
        </div>
        <div className="wrap mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {ctList('enterprises.pillars', PILLARS).map((p, i) => (
            <Reveal key={p.t} delay={i * 0.05}>
              <Link to={p.to} data-testid={`pillar-${i}`} className="group flex h-full flex-col rounded-3xl border border-navy/10 bg-white p-8 transition-[transform,box-shadow] duration-500 hover:-translate-y-1 hover:shadow-xl hover:shadow-navy/10">
                <h3 className="text-lg font-semibold tracking-tight group-hover:text-electric">{p.t}</h3>
                <p className="mt-2.5 text-sm leading-relaxed text-navy/65">{p.d}</p>
                <span className="mt-6 inline-flex items-center gap-1.5 text-sm font-semibold text-electric transition-colors group-hover:text-navy">
                  Explore <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5" />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Services stack */}
      <section id="services" className="bg-white py-20 sm:py-28">
        <div className="wrap">
          <SectionTag>02 — Enterprise Services</SectionTag>
          <Reveal delay={0.05}><h2 className="text-h2 mt-5 font-semibold">A Connected Enterprise <span className="font-serif italic font-normal text-electric">Capability Stack</span></h2></Reveal>
          <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {ctList('enterprises.services', SERVICES).map((s, i) => (
              <Reveal key={s.title} delay={i * 0.05} className="rounded-3xl border border-navy/10 bg-paper p-8">
                <span className="font-mono text-xs text-electric/60">0{i + 1}</span>
                <h3 className="mt-3 text-lg font-semibold tracking-tight">{s.title}</h3>
                <ul className="mt-4 space-y-2">
                  {s.items.map((x) => (
                    <li key={x} className="flex items-center gap-2.5 text-sm text-navy/70"><span className="h-1 w-1 shrink-0 rounded-full bg-electric" />{x}</li>
                  ))}
                </ul>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Image + CTA band */}
      <section className="bg-grid-dark relative overflow-hidden bg-navy py-20 text-white sm:py-28">
        <div className="wrap grid items-center gap-12 lg:grid-cols-2">
          <Reveal>
            <div className="overflow-hidden rounded-3xl border border-white/10 shadow-2xl shadow-black/40">
              <img src={IMAGES.boardroom} alt="Enterprise leadership" className="aspect-[4/3] w-full object-cover" loading="lazy" />
            </div>
          </Reveal>
          <div>
            <SectionTag dark>03 — Begin</SectionTag>
            <Reveal delay={0.05}><h2 className="text-h2 mt-5 font-semibold">Bring your requirements. <span className="font-serif italic font-normal text-gold">Leave with capability.</span></h2></Reveal>
            <Reveal delay={0.1} className="mt-8 flex flex-wrap gap-4">
              <ArrowBtn onClick={() => open('Enterprise Membership')} variant="gold" testId="ent-cta-enquire">Enquire About Membership</ArrowBtn>
              <ArrowBtn to="/contact?role=enterprise" variant="ghostLight" testId="ent-cta-contact">Contact DITEC</ArrowBtn>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Enterprise FAQ */}
      <section id="faq" className="bg-paper py-20 sm:py-28">
        <div className="wrap max-w-4xl">
          <SectionTag>04 — Enterprise FAQ</SectionTag>
          <Reveal delay={0.1} className="mt-12"><FAQ items={ENTERPRISE_FAQ} testId="enterprise-faq" /></Reveal>
        </div>
      </section>
    </div>
  );
}
